import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import HelmetMeta from "../../components/HelmetMeta";
import { useGetDocTypeListWithChildren } from "../../shared/hooks/useDocTypesData";
import { useSetStatusCommand } from "../../shared/hooks/useProductionCenter";
import {
    useGetUserSettings,
    useSaveUserSettings,
} from "../../shared/hooks/useFrappeDoctype";
import { useStyles as usePosStyles } from "../PosPage/styles";
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import OrderCard from "./components/OrderCard";
import ProductionCenterHeader from "./components/Header";
import { AppContext } from "../../AppContext";
import { useProductionCenterRealtime } from "../../shared/hooks/useProductionCenterRealtime";
import { useTranslation } from "react-i18next";

function ProductionCenter({ detailedCards = false }) {
    const { t } = useTranslation();
    const classes = usePosStyles();
    const [focusMode, setFocusMode] = useState({ active: false, orders: [] });

    const { socket } = useContext(AppContext);

    const { data: productionCenters, isLoading: pcLoading } =
        useGetDocTypeListWithChildren({
        doctype: "Restaurant Object",
        filters: [["type", "=", "Production Center"]],
    });

    const {
        data: attendingOrders,
        isLoading: ordersLoading,
        refetch: refetchOrders,
    } = useGetDocTypeListWithChildren({
        doctype: "Table Order",
        filters: [["status", "=", "Attending"]],
    });

    useEffect(() => {
        if (socket) {
            socket.emit("doctype_subscribe", "Table Order");

            socket.on("list_update", (data) => {
                if (data.doctype == "Table Order") {
                    console.log("refetching tables data");
                    refetchOrders()
                    // refetchTablesData();
                }
            });

            socket.on("table_order_voided", () => {
                
            })
        }
    }, [socket]);

    const { mutate: setStatusCommand, isLoading: isMutating } =
        useSetStatusCommand();

    const [selectedCenterNames, setSelectedCenterNames] = useState([]);

    const selectedCenters = useMemo(() => {
        if (!productionCenters || !selectedCenterNames?.length) return [];
        return selectedCenterNames
            .map((n) => productionCenters.find((pc) => pc.name === n))
            .filter(Boolean);
    }, [productionCenters, selectedCenterNames]);

    useEffect(() => {
        if (selectedCenters?.length)
            console.log(selectedCenters.map((c) => c.status_managed));
    }, [selectedCenters]);

    const normalizeGroup = (s) => (s ? String(s).trim() : "");
    const allowedGroups = useMemo(() => {
        if (!selectedCenters?.length) return new Set();
        const set = new Set();
        selectedCenters.forEach((c) => {
            (c.production_center_group || [])
                .map((g) => normalizeGroup(g.item_group))
                .filter((g) => g && g.length > 0)
                .forEach((g) => set.add(g));
        });
        return set;
    }, [selectedCenters]);

    // Build ordered unique status options from status_managed and next_status
    const statusOptions = useMemo(() => {
        if (!selectedCenters?.length) return [];
        const seen = new Set();
        const ordered = [];
        selectedCenters.forEach((center) => {
            const list = center.status_managed || [];
            list.forEach((row) => {
                if (row.status_managed && !seen.has(row.status_managed)) {
                    seen.add(row.status_managed);
                    ordered.push(row.status_managed);
                }
                if (row.next_status && !seen.has(row.next_status)) {
                    seen.add(row.next_status);
                    ordered.push(row.next_status);
                }
            });
        });
        return ordered;
    }, [selectedCenters]);

    const [selectedStatuses, setSelectedStatuses] = useState([]);

    // Map of status -> next_status (if any)
    const transitionMap = useMemo(() => {
        const map = new Map();
        selectedCenters.forEach((center) => {
            (center.status_managed || []).forEach((row) => {
                if (row?.status_managed && row?.next_status) {
                    if (!map.has(row.status_managed)) {
                        map.set(row.status_managed, row.next_status);
                    }
                }
            });
        });
        return map;
    }, [selectedCenters]);

    const canAdvanceStatus = (status) => {
        const next = transitionMap.get(status);
        return !!next;
    };

    const filteredOrders = useMemo(() => {
        if (!selectedCenters?.length) return [];
        const orders = attendingOrders || [];
        const result = [];

        const allowByGroup = (group) => {
            const g = normalizeGroup(group);
            if (!g) return false;
            if (g === "All Item Groups") return true;
            return allowedGroups.size === 0 || allowedGroups.has(g);
        };

        orders.forEach((order) => {
            const all = order.entry_items || [];

            // Build map by identifier to traverse parent chains
            const byId = new Map();
            all.forEach((it) => {
                if (it.identifier) byId.set(it.identifier, it);
            });

            const getRootId = (it) => {
                let current = it;
                let safety = 0;
                while (
                    safety < 10 &&
                    current &&
                    current.parent_item &&
                    current.parent_item !== "NULL" &&
                    byId.has(current.parent_item)
                ) {
                    current = byId.get(current.parent_item);
                    safety += 1;
                }
                return current?.identifier || it.identifier || null;
            };

            // Cluster items by root parent (1-minute groups handled later in card)
            const clusters = new Map(); // rootId -> { items: [], allowed: bool }
            all.forEach((it) => {
                const rootId = getRootId(it) || it.identifier || it.name;
                if (!clusters.has(rootId)) {
                    clusters.set(rootId, { items: [] });
                }
                clusters.get(rootId).items.push(it);
            });

            // Determine if cluster is allowed (any member is in allowed groups)
            clusters.forEach((cluster) => {
                cluster.allowed = cluster.items.some((it) =>
                    allowByGroup(it.item_group)
                );
            });

            // Collect items from allowed clusters matching selected statuses (or all if focus mode)
            const kept = [];
            clusters.forEach((cluster) => {
                if (!cluster.allowed) return;
                cluster.items.forEach((it) => {
                    if (!focusMode.active) {
                        if (
                            selectedStatuses.length > 0 &&
                            !selectedStatuses.includes(it.status)
                        )
                            return;
                    }
                    kept.push(it);
                });
            });

            if (kept.length > 0) {
                result.push({ ...order, entry_items: kept });
            }
        });

        return result;
    }, [
        attendingOrders,
        allowedGroups,
        selectedStatuses,
        selectedCenters,
        focusMode.active,
    ]);

    const totalPendingItems = useMemo(() => {
        return filteredOrders.reduce(
            (acc, o) => acc + (o.entry_items ? o.entry_items.length : 0),
            0
        );
    }, [filteredOrders]);

    useEffect(() => {
        if (filteredOrders?.length > 0 && attendingOrders?.length > 0) {
            console.log({
                filteredOrders: filteredOrders.find(
                    (i) => i.name == "OR-2025-02257"
                ),
                attendingOrders: attendingOrders.find(
                    (i) => i.name == "OR-2025-02257"
                ),
            });
        }
    }, [filteredOrders, attendingOrders]);

    const advanceItems = (orderName, identifiers) => {
        if (!identifiers || identifiers.length === 0) return;
        const centerName =
            selectedCenters && selectedCenters.length > 0
                ? selectedCenters[0].name
                : null;
        if (!centerName) return;
        setStatusCommand(
            {
                production_center: centerName,
                identifiers,
                orderName,
            },
            {
                onSuccess: () => {
                    refetchOrders();
                },
            }
        );
    };

    const handleAdvanceAllForOrder = (order) => {
        const ids = (order.entry_items || [])
            .map((i) => i.identifier)
            .filter(Boolean);
        advanceItems(order.name, ids);
    };

    const handleAdvanceItem = (order, item) => {
        if (!item?.identifier) return;
        const ids = [item.identifier];
        // Include addons (children) present in the current order entry_items
        (order.entry_items || []).forEach((it) => {
            if (it.parent_item && it.parent_item === item.identifier && it.identifier) {
                ids.push(it.identifier);
            }
        });
        advanceItems(order.name, ids);
    };

    // Persist last selected production center in user settings (doctype: production_center_settings)
    const { data: pcUserSettings } = useGetUserSettings({
        doctype: "production_center_settings",
    });
    const saveUserSettings = useSaveUserSettings();

    // Load last selection (multi)
    useEffect(() => {
        if (
            (!selectedCenterNames || selectedCenterNames.length === 0) &&
            pcUserSettings &&
            productionCenters
        ) {
            const lastArr = pcUserSettings.last_selected_centers;
            if (Array.isArray(lastArr) && lastArr.length > 0) {
                const valid = lastArr.filter((n) =>
                    productionCenters.find((pc) => pc.name === n)
                );
                if (valid.length > 0) {
                    setSelectedCenterNames(valid);
                    return;
                }
            }
            const last = pcUserSettings.last_selected_center;
            if (last && productionCenters.find((pc) => pc.name === last)) {
                setSelectedCenterNames([last]);
            } else if (productionCenters?.length) {
                setSelectedCenterNames([productionCenters[0].name]);
            }
        }
    }, [pcUserSettings, productionCenters, selectedCenterNames]);

    const persistSelectedStatuses = (centerName, statuses) => {
        const newSettings = {
            ...(pcUserSettings || {}),
            selected_statuses_by_center: {
                ...(pcUserSettings?.selected_statuses_by_center || {}),
                [centerName]: statuses,
            },
        };
        saveUserSettings.mutate({
            doctype: "production_center_settings",
            user_settings: newSettings,
        });
    };

    // When centers or options change, select saved statuses for first center or default
    useEffect(() => {
        if (!selectedCenterNames || selectedCenterNames.length === 0) return;
        const centerName = selectedCenterNames[0];
        const savedArray =
            pcUserSettings?.selected_statuses_by_center?.[centerName];
        const savedSingle =
            pcUserSettings?.selected_status_by_center?.[centerName];
        if (Array.isArray(savedArray) && savedArray.length > 0) {
            const valid = savedArray.filter((s) => statusOptions.includes(s));
            if (valid.length > 0) {
                setSelectedStatuses(valid);
                return;
            }
        } else if (savedSingle && statusOptions.includes(savedSingle)) {
            setSelectedStatuses([savedSingle]);
            return;
        }
        const first = statusOptions[0] ? [statusOptions[0]] : [];
        setSelectedStatuses(first);
        if (first.length > 0) {
            persistSelectedStatuses(centerName, first);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCenterNames, statusOptions, pcUserSettings]);

    // Save on change (multi)
    useEffect(() => {
        if (selectedCenterNames && selectedCenterNames.length > 0) {
            const newSettings = {
                ...(pcUserSettings || {}),
                last_selected_centers: selectedCenterNames,
            };
            saveUserSettings.mutate({
                doctype: "production_center_settings",
                user_settings: newSettings,
            });
        }
    }, [selectedCenterNames]);

    const isLoading = pcLoading || ordersLoading;

    // Selection / fullscreen state
    const [selectedOrders, setSelectedOrders] = useState([]); // array of order names
    const isOrderSelected = (o) => selectedOrders.includes(o.name);
    const toggleOrderSelection = (o) => {
        setSelectedOrders((prev) =>
            prev.includes(o.name)
                ? prev.filter((id) => id !== o.name)
                : [...prev, o.name]
        );
    };
    const expandSingleOrder = (o) => {
        // Quick single-expand: focus this order with all statuses toggled
        setFocusMode({ active: true, orders: [o.name] });
    };

    const expandSelectedOrders = () => {
        if (selectedOrders.length >= 2) {
            setFocusMode({ active: true, orders: [...selectedOrders] });
        }
    };

    const exitFocus = () => {
        setFocusMode({ active: false, orders: [] });
        setSelectedOrders([]);
    };

    useEffect(() => {
        console.log(filteredOrders)
    },[filteredOrders])

    // Hook: realtime toasts + expansion + highlight
    const [highlightOrders, setHighlightOrders] = useState([]);
    const [highlightItemsByOrder, setHighlightItemsByOrder] = useState({});
    const prevEntryMapRef = useRef(new Map());
    useProductionCenterRealtime({
        socket,
        orders: attendingOrders,
        refetchOrders,
        onExpandOrder: (orderName) => setFocusMode({ active: true, orders: [orderName] }),
        onOrdersAdded: (names) => {
            if (!Array.isArray(names) || names.length === 0) return;
            setHighlightOrders((prev) => Array.from(new Set([...prev, ...names])));
            setTimeout(() => {
                setHighlightOrders((prev) => prev.filter((n) => !names.includes(n)));
            }, 4000);
        },
    });

    // Detect added/deleted entry_items per order
    useEffect(() => {
        const nextMap = new Map();
        (attendingOrders || []).forEach((o) => {
            const ids = new Set((o.entry_items || []).map((i) => i.identifier).filter(Boolean));
            nextMap.set(o.name, ids);
        });

        const addedByOrder = {};
        nextMap.forEach((idSet, orderName) => {
            const prevSet = prevEntryMapRef.current.get(orderName) || new Set();
            const added = [];
            idSet.forEach((id) => {
                if (!prevSet.has(id)) added.push(id);
            });
            if (added.length > 0) {
                addedByOrder[orderName] = new Set(added);
            }
        });

        if (Object.keys(addedByOrder).length > 0) {
            setHighlightItemsByOrder((prev) => ({ ...prev, ...addedByOrder }));
            setTimeout(() => {
                setHighlightItemsByOrder((prev) => {
                    const clone = { ...prev };
                    Object.keys(addedByOrder).forEach((k) => delete clone[k]);
                    return clone;
                });
            }, 3000);
        }

        prevEntryMapRef.current = nextMap;
    }, [attendingOrders]);

    return (
        <React.Fragment>
            <HelmetMeta
                title={
                    detailedCards
                        ? t("kdsTabs.helmetTitle")
                        : t("productionCenter.helmetTitle")
                }
            />
            <div className={classes.PageWrap}>
                <div className={classes.NewOrderPage}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                        }}
                    >
                        <ProductionCenterHeader
                            isLoading={isLoading}
                            productionCenters={productionCenters}
                            selectedCenterName={selectedCenterNames}
                            onChangeCenter={setSelectedCenterNames}
                            onRefresh={() => refetchOrders()}
                            statusOptions={statusOptions}
                            selectedStatuses={selectedStatuses}
                            onChangeStatuses={(next) => {
                                setSelectedStatuses(next);
                                if (
                                    selectedCenterNames &&
                                    selectedCenterNames.length > 0
                                ) {
                                    persistSelectedStatuses(
                                        selectedCenterNames[0],
                                        next
                                    );
                                }
                            }}
                            totalPendingItems={totalPendingItems}
                            selectionCount={selectedOrders.length}
                            focusActive={focusMode.active}
                            onExpandSelected={expandSelectedOrders}
                            onExitFocus={exitFocus}
                            selectedOrderObjects={selectedOrders
                                .map((name) =>
                                    filteredOrders.find((o) => o.name === name)
                                )
                                .filter(Boolean)}
                            onReorderSelectedOrders={(orderedNames) =>
                                setSelectedOrders(orderedNames)
                            }
                        />

                        {isLoading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mt: 8,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}

                        {!isLoading &&
                            (!selectedCenterNames ||
                                selectedCenterNames.length === 0) && (
                                <Box sx={{ mt: 8, textAlign: "center" }}>
                                    <Typography variant="h6">
                                        {t("productionCenter.selectCenterHint")}
                                    </Typography>
                                </Box>
                            )}

                        {!isLoading &&
                            selectedCenterNames &&
                            selectedCenterNames.length > 0 &&
                            filteredOrders.length === 0 && (
                                <Box sx={{ mt: 8, textAlign: "center" }}>
                                    <Typography variant="h6">
                                        {t("productionCenter.noItemsToPrepare")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#6E7187" }}
                                    >
                                        {t("productionCenter.allCaughtUp")}
                                    </Typography>
                                </Box>
                            )}

                        {!isLoading &&
                            selectedCenterNames &&
                            selectedCenterNames.length > 0 &&
                            filteredOrders.length > 0 && (
                                <Grid
                                    container
                                    spacing={2}
                                    style={{ overflowY: "auto" }}
                                >
                                    {(focusMode.active
                                        ? filteredOrders.filter((o) =>
                                              focusMode.orders.includes(o.name)
                                          )
                                        : filteredOrders
                                    ).map((order) => (
                                        <Grid
                                            key={order.name}
                                            item
                                            xs={focusMode.active ? 12 : 12}
                                            md={
                                                focusMode.active
                                                    ? 12
                                                    : detailedCards
                                                    ? 12
                                                    : 6
                                            }
                                            lg={
                                                focusMode.active
                                                    ? 12
                                                    : detailedCards
                                                    ? 6
                                                    : 4
                                            }
                                            xl={
                                                focusMode.active
                                                    ? 12
                                                    : detailedCards
                                                    ? 4
                                                    : 3
                                            }
                                        >
                                            <OrderCard
                                                order={order}
                                                isMutating={isMutating}
                                                selectedStatuses={
                                                    focusMode.active
                                                        ? statusOptions
                                                        : selectedStatuses
                                                }
                                                selectedCenters={selectedCenters}
                                                detailed={detailedCards}
                                                canAdvanceStatus={
                                                    canAdvanceStatus
                                                }
                                                onAdvanceIdentifiers={(
                                                    ord,
                                                    ids
                                                ) =>
                                                    advanceItems(ord.name, ids)
                                                }
                                                onAdvanceItem={
                                                    handleAdvanceItem
                                                }
                                                selected={isOrderSelected(
                                                    order
                                                )}
                                                onToggleSelect={
                                                    toggleOrderSelection
                                                }
                                                onExpand={expandSingleOrder}
                                                highlight={highlightOrders.includes(order.name)}
                                                highlightItemIds={highlightItemsByOrder[order.name]}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                    </Box>
                </div>
            </div>
        </React.Fragment>
    );
}

export default ProductionCenter;
